import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  /**
   * Ultima larghezza reale del marquee.
   * Serve per ignorare i "resize" verticali generati
   * dalle barre del browser mobile durante lo scroll.
   *
   * @type {number}
   */
  #lastWidth = 0;

  /**
   * Indica se il dispositivo supporta realmente l'hover.
   * Su touch disabilitiamo slowdown/speedup.
   *
   * @type {boolean}
   */
  #hoverEnabled = false;

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;

    if (marqueeItems.length === 0) return;

    /*
     * Costruzione iniziale marquee.
     */
    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();

    /*
     * Memorizziamo la larghezza iniziale.
     * Da questo momento reagiremo soltanto
     * a veri cambiamenti di larghezza.
     */
    this.#lastWidth = this.offsetWidth;

    /*
     * Hover slowdown soltanto su dispositivi
     * che hanno davvero mouse/trackpad.
     *
     * Evita comportamenti anomali su iPhone/Android
     * durante touch e scroll.
     */
    this.#hoverEnabled =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    window.addEventListener('resize', this.#handleResize);

    if (this.#hoverEnabled) {
      this.addEventListener('pointerenter', this.#slowDown);
      this.addEventListener('pointerleave', this.#speedUp);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#handleResize);

    if (this.#hoverEnabled) {
      this.removeEventListener('pointerenter', this.#slowDown);
      this.removeEventListener('pointerleave', this.#speedUp);
    }

    /*
     * Evitiamo che rimangano animazioni JS pendenti.
     */
    this.#slowDown.cancel();
    this.#animation?.cancel();
    this.#animation = null;
  }

  #slowDown = debounce(() => {
    /*
     * Safety ulteriore:
     * niente slowdown sui dispositivi touch.
     */
    if (!this.#hoverEnabled) return;

    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp = () => {
    if (!this.#hoverEnabled) return;

    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;

    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  };

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    /*
     * Protezione contro valori NaN / Infinity / 0.
     */
    const safeValue =
      Number.isFinite(value) && value > 0 ? value : 25;

    this.style.setProperty('--marquee-speed', `${safeValue}s`);
  }

  #calculateSpeed() {
    const configuredSpeedFactor = Number(
      this.getAttribute('data-speed-factor')
    );

    const speedFactor =
      Number.isFinite(configuredSpeedFactor) &&
      configuredSpeedFactor > 0
        ? configuredSpeedFactor
        : 25;

    const { marqueeItems } = this.refs;

    const marqueeWidth = this.offsetWidth;

    const marqueeRepeatedItemWidth =
      marqueeItems[0]?.offsetWidth ?? 1;

    const count =
      marqueeRepeatedItemWidth <= 0
        ? 1
        : Math.max(
            1,
            Math.ceil(
              marqueeWidth / marqueeRepeatedItemWidth
            )
          );

    return Math.sqrt(count) * speedFactor;
  }

  /**
   * FIX MOBILE
   *
   * Safari/Chrome mobile possono emettere "resize"
   * quando la barra URL superiore/inferiore cambia
   * altezza durante lo scroll.
   *
   * In quei casi la larghezza NON cambia.
   *
   * Prima il marquee veniva comunque:
   * - ricostruito
   * - duplicato
   * - riavviato
   *
   * e poteva rimanere temporaneamente vuoto.
   *
   * Ora ignoriamo completamente i resize
   * che non modificano realmente la larghezza.
   */
  #handleResize = debounce(() => {
    if (!this.isConnected) return;

    const { marqueeItems } = this.refs;

    if (!marqueeItems || marqueeItems.length === 0) {
      return;
    }

    const newWidth = this.offsetWidth;

    if (
      !Number.isFinite(newWidth) ||
      newWidth <= 0
    ) {
      return;
    }

    /*
     * Se la larghezza è sostanzialmente identica,
     * è quasi certamente un resize verticale mobile.
     *
     * 2px di tolleranza evita micro-variazioni
     * dovute al rendering/subpixel.
     */
    if (
      this.#lastWidth > 0 &&
      Math.abs(newWidth - this.#lastWidth) < 2
    ) {
      return;
    }

    this.#lastWidth = newWidth;

    const newNumberOfCopies =
      this.#calculateNumberOfCopies();

    const currentNumberOfCopies =
      marqueeItems.length;

    if (
      !Number.isFinite(newNumberOfCopies) ||
      newNumberOfCopies < 1
    ) {
      return;
    }

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(
        newNumberOfCopies - currentNumberOfCopies
      );
    } else if (
      newNumberOfCopies < currentNumberOfCopies
    ) {
      this.#removeRepeatedItems(
        currentNumberOfCopies - newNumberOfCopies
      );
    }

    /*
     * Ricostruiamo solamente dopo un VERO
     * resize orizzontale, ad esempio:
     * - rotazione telefono
     * - resize finestra desktop
     */
    this.#duplicateContent();
    this.#setSpeed();
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    /*
     * Recuperiamo le animazioni DOPO che il browser
     * ha avuto un frame per aggiornare il layout.
     */
    requestAnimationFrame(() => {
      if (!this.isConnected) return;

      const animations =
        this.refs.wrapper.getAnimations();

      for (const animation of animations) {
        animation.currentTime = 0;

        /*
         * Safety:
         * se per qualche motivo era rimasta rallentata,
         * la rimettiamo alla velocità normale.
         */
        animation.updatePlaybackRate(1);
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (
      this.refs.content.cloneNode(true)
    );

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(
    numberOfCopies = this.#calculateNumberOfCopies()
  ) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    const safeNumberOfCopies = Math.max(
      1,
      Number.isFinite(numberOfCopies)
        ? Math.ceil(numberOfCopies)
        : 1
    );

    for (
      let i = 0;
      i < safeNumberOfCopies - 1;
      i++
    ) {
      const clone =
        marqueeItems[0].cloneNode(true);

      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(
    numberOfCopies = this.#calculateNumberOfCopies()
  ) {
    const { content } = this.refs;

    const safeNumberOfCopies = Math.max(
      0,
      Number.isFinite(numberOfCopies)
        ? Math.floor(numberOfCopies)
        : 0
    );

    for (
      let i = 0;
      i < safeNumberOfCopies;
      i++
    ) {
      /*
       * Non eliminiamo mai l'ultimo elemento originale.
       */
      if (content.children.length <= 1) break;

      content.lastElementChild?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const { marqueeItems } = this.refs;

    const marqueeWidth = this.offsetWidth;

    const marqueeRepeatedItemWidth =
      marqueeItems[0]?.offsetWidth ?? 1;

    if (
      !Number.isFinite(marqueeWidth) ||
      marqueeWidth <= 0
    ) {
      return 1;
    }

    if (
      !Number.isFinite(marqueeRepeatedItemWidth) ||
      marqueeRepeatedItemWidth <= 0
    ) {
      return 1;
    }

    return Math.max(
      1,
      Math.ceil(
        marqueeWidth / marqueeRepeatedItemWidth
      )
    );
  }
}

/**
 * Animate a numeric property smoothly.
 *
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration in milliseconds.
 * @param {function(number): void} params.onUpdate
 * @param {function(number): number} [params.easing]
 * @param {function(): void} [params.onComplete]
 */
function animateValue({
  from,
  to,
  duration,
  onUpdate,
  easing = (t) => t * t * (3 - 2 * t),
  onComplete,
}) {
  const startTime = performance.now();

  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;

    const progress = Math.min(
      elapsed / duration,
      1
    );

    const easedProgress = easing(progress);

    currentValue =
      from +
      (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (
      typeof onComplete === 'function'
    ) {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },

    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define(
    'marquee-component',
    MarqueeComponent
  );
}